import { useEffect, useState } from 'react';
import { Sparkles, Star, Gift } from 'lucide-react';

export default function PointsAnimation({ points, totalPoints = 0, threshold = 10, discountValue = 5, onComplete }) {
  const [stage, setStage] = useState('enter'); // enter, counting, celebrate, exit
  const [displayPoints, setDisplayPoints] = useState(0);
  const [confetti, setConfetti] = useState([]);
  const safeThreshold = Math.max(1, Number(threshold) || 10);
  const safeTotalPoints = Math.max(0, Number(totalPoints) || 0);
  const rewardAvailable = safeTotalPoints >= safeThreshold;
  const moduloPoints = safeTotalPoints % safeThreshold;
  const progressPoints = rewardAvailable && moduloPoints === 0 ? safeThreshold : moduloPoints;
  const remainingPoints = moduloPoints === 0 ? safeThreshold : safeThreshold - moduloPoints;

  useEffect(() => {
    // Generate confetti particles
    const particles = Array.from({ length: 20 }, (_, i) => ({
      id: i,
      left: Math.random() * 100,
      delay: Math.random() * 0.5,
      duration: 1 + Math.random() * 1,
      color: ['#C5A059', '#5D1822', '#4A5D23', '#FFD700'][Math.floor(Math.random() * 4)]
    }));
    setConfetti(particles);

    // Animation sequence
    const enterTimeout = setTimeout(() => {
      setStage('counting');
      
      // Count up animation
      let current = 0;
      const increment = Math.ceil(points / 20);
      const countInterval = setInterval(() => {
        current += increment;
        if (current >= points) {
          setDisplayPoints(points);
          clearInterval(countInterval);
          setStage('celebrate');
          
          // Celebrate then exit
          setTimeout(() => {
            setStage('exit');
            setTimeout(() => {
              onComplete && onComplete();
            }, 500);
          }, 2000);
        } else {
          setDisplayPoints(current);
        }
      }, 50);
    }, 300);

    return () => clearTimeout(enterTimeout);
  }, [points, onComplete]);

  return (
    <div className={`fixed inset-0 z-[200] flex items-center justify-center transition-opacity duration-500 ${
      stage === 'exit' ? 'opacity-0' : 'opacity-100'
    }`}>
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      
      {/* Confetti */}
      {(stage === 'counting' || stage === 'celebrate') && confetti.map(particle => (
        <div
          key={particle.id}
          className="absolute w-3 h-3 rounded-full animate-confetti"
          style={{
            left: `${particle.left}%`,
            top: '-10px',
            backgroundColor: particle.color,
            animationDelay: `${particle.delay}s`,
            animationDuration: `${particle.duration}s`
          }}
        />
      ))}
      
      {/* Main content */}
      <div className={`relative transform transition-all duration-500 ${
        stage === 'enter' ? 'scale-50 opacity-0' : 'scale-100 opacity-100'
      }`}>
        <div className="bg-white rounded-3xl p-8 shadow-2xl text-center max-w-sm mx-4">
          {/* Stars decoration */}
          <div className="absolute -top-6 left-1/2 -translate-x-1/2">
            <div className={`transform transition-transform duration-500 ${
              stage === 'celebrate' ? 'scale-125' : 'scale-100'
            }`}>
              <div className="relative">
                <Star className="w-12 h-12 text-[#C5A059] fill-[#C5A059] animate-pulse" />
                <Sparkles className="w-6 h-6 text-[#FFD700] absolute -top-2 -right-2 animate-spin-slow" />
              </div>
            </div>
          </div>
          
          {/* Content */}
          <div className="mt-4">
            <p className="text-[#5A5A5A] text-sm mb-2">Hai guadagnato</p>
            
            <div className={`text-6xl font-bold text-[#5D1822] mb-2 transition-transform duration-300 ${
              stage === 'celebrate' ? 'animate-bounce-once' : ''
            }`}>
              +{displayPoints}
            </div>
            
            <p className="text-[#C5A059] font-semibold text-lg mb-4">
              {points === 1 ? 'PUNTO' : 'PUNTI'}
            </p>
            
            {stage === 'celebrate' && (
              <div className="animate-fade-in">
                <div className="flex items-center justify-center gap-2 text-[#4A5D23] bg-[#4A5D23]/10 rounded-full px-4 py-2">
                  <Gift className="w-5 h-5" />
                  <span className="text-sm font-medium">
                    {rewardAvailable
                      ? `Hai uno sconto da €${Number(discountValue || 5).toFixed(2)} disponibile!`
                      : `${remainingPoints} punti al prossimo sconto`}
                  </span>
                </div>
              </div>
            )}
          </div>
          
          {/* Progress ring */}
          <div className="mt-6 flex justify-center">
            <div className="relative w-20 h-20">
              <svg className="w-20 h-20 transform -rotate-90">
                <circle
                  cx="40"
                  cy="40"
                  r="36"
                  stroke="#E5E5E5"
                  strokeWidth="6"
                  fill="none"
                />
                <circle
                  cx="40"
                  cy="40"
                  r="36"
                  stroke="#C5A059"
                  strokeWidth="6"
                  fill="none"
                  strokeLinecap="round"
                  strokeDasharray={`${(progressPoints / safeThreshold) * 226} 226`}
                  className="transition-all duration-300"
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-sm font-bold text-[#5D1822]">{progressPoints}/{safeThreshold}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes confetti {
          0% {
            transform: translateY(0) rotate(0deg);
            opacity: 1;
          }
          100% {
            transform: translateY(100vh) rotate(720deg);
            opacity: 0;
          }
        }
        .animate-confetti {
          animation: confetti linear forwards;
        }
        @keyframes bounce-once {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.2); }
        }
        .animate-bounce-once {
          animation: bounce-once 0.5s ease-in-out;
        }
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fade-in 0.3s ease-out;
        }
        @keyframes spin-slow {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .animate-spin-slow {
          animation: spin-slow 3s linear infinite;
        }
      `}</style>
    </div>
  );
}
